import ChevronDownIcon from "@redotech/redo-icons/chevron-down.svg";
import ChevronRightIcon from "@redotech/redo-icons/chevron-right.svg";
import { RedoIcon } from "@redotech/redo-web/arbiter-components/icon/redo-icon";
import { RedoProgressBar } from "@redotech/redo-web/arbiter-components/progress-bar/redo-progress-bar";
import {
  RedoTablePaddedCell,
  RedoTableSkeletonTextCell,
} from "@redotech/redo-web/arbiter-components/tables/redo-table-cells";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import type { SpacingValue } from "@redotech/redo-web/theme/box";
import type {
  TextColorValue,
  TextWeightValue,
} from "@redotech/redo-web/theme/typography";
import { memo } from "react";

/** Parents indent by depth; a leaf takes one more step where the chevron sits. */
const INDENT: readonly SpacingValue[] = ["none", "xl", "4xl", "6xl", "7xl"];

export const TreeLabelCell = memo(function TreeLabelCell({
  label,
  depth,
  expandable,
  collapsed,
}: {
  label: string;
  depth: number;
  expandable: boolean;
  collapsed: boolean;
}) {
  return (
    <RedoTablePaddedCell>
      <Flex align="center" gap="xs" pl={INDENT[expandable ? depth : depth + 1]}>
        {expandable && (
          <RedoIcon
            color="tertiary"
            Icon={collapsed ? ChevronRightIcon : ChevronDownIcon}
            size={16}
          />
        )}
        <Text fontSize="sm" fontWeight={expandable ? "medium" : "regular"}>
          {label}
        </Text>
      </Flex>
    </RedoTablePaddedCell>
  );
});

export const BarCell = memo(function BarCell({
  percentage,
  loading,
}: {
  percentage: number;
  loading: boolean;
}) {
  if (loading) {
    return <RedoTableSkeletonTextCell length={24} />;
  }
  return (
    <RedoTablePaddedCell>
      <Flex dir="column" grow={1} justify="center" w="full">
        <RedoProgressBar
          labelStyle="none"
          percentage={percentage}
          style="extra-thin"
        />
      </Flex>
    </RedoTablePaddedCell>
  );
});

export const NumberCell = memo(function NumberCell({
  text,
  loading,
  textColor = "primary",
  fontWeight = "regular",
}: {
  text: string;
  loading: boolean;
  textColor?: TextColorValue;
  fontWeight?: TextWeightValue;
}) {
  if (loading) {
    return <RedoTableSkeletonTextCell length={8} />;
  }
  return (
    <Flex p="xl">
      <Text fontSize="xs" fontWeight={fontWeight} textColor={textColor}>
        {text}
      </Text>
    </Flex>
  );
});
